import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  ThemeProvider,
  createTheme,
  Paper,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
// import EventIcon from "@mui/icons-material/Event";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { TokenContext } from "../context/TokenContext";
import { afasPoster } from "../hooks/useAfasDataMutation";

// Simple email & phone validators (zelfde als brochure)
const isEmail = (v: string) => /.+@.+\..+/.test(v.trim());
const isPhone = (v: string) => /^(\+\d{6,}|0\d{9,})$/.test(v.trim());

export interface CompanyInfo {
  id: number;
  name: string;
}

const callTimeOptions = ["Ochtend (09:00 – 11:00)", "Middag (13:00 – 15:00)", "Einde dag (15:00 – 17:00)"];

const callReasonOptions = [
  "Oriëntatie op nieuwe software",
  "Vervanging huidige oplossing",
  "Uitbreiding bestaande software",
  "Overig",
];

export default function AfspraakForm() {
  const token = useContext(TokenContext);

  const referrerTest = document.referrer;
  console.log({ referrerTest });
  const referer = "https://www.afas.nl/afspraak";

  const [values, setValues] = useState({
    callDate: "",
    callTime: "",
    callReason: "",
    firstName: "",
    lastName: "",
    email: "",
    orgName: "",
    employees: "",
    phone: "",
    type: "afspraak",
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const errors = useMemo(() => {
    return {
      callDate: values.callDate.trim() ? "" : "Vereist",
      callTime: values.callTime.trim() ? "" : "Vereist",
      callReason: values.callReason.trim() ? "" : "Vereist",
      firstName: values.firstName.trim() ? "" : "Vereist",
      lastName: values.lastName.trim() ? "" : "Vereist",
      email: values.email.trim() ? (isEmail(values.email) ? "" : "Ongeldig e-mailadres") : "Vereist",
      orgName: values.orgName.trim() ? "" : "Vereist",
      employees: values.employees ? "" : "Vereist",
      phone: values.phone.trim() ? (isPhone(values.phone) ? "" : "Ongeldig telefoonnummer") : "Vereist",
    } as const;
  }, [values]);

  const [suggestedCompanies, setSuggestedCompanies] = useState<CompanyInfo[]>([]);
  const [companyInput, setCompanyInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!searchTerm || searchTerm.trim().length <= 2) {
      setSuggestedCompanies([]);
      setIsLoadingCompanies(false);
      return;
    }

    const handle = setTimeout(async () => {
      const orgName = searchTerm.trim();

      try {
        setIsLoadingCompanies(true);
        const path = "company";
        const arg = { token, body: orgName };
        const response = await afasPoster(path, { arg });

        console.log("company response", response);

        let companies: CompanyInfo[] = [];

        if (Array.isArray(response)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          companies = response.map((c: any) => ({
            id: Number(c.id),
            name: String(c.name),
          }));
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } else if (Array.isArray((response as any).companies)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          companies = (response as any).companies.map((c: any) => ({
            id: Number(c.id),
            name: String(c.name),
          }));
        } else {
          console.warn("Unexpected company response shape", response);
        }

        setSuggestedCompanies(companies);
      } catch (e) {
        console.error("Error fetching company suggestions", e);
        setSuggestedCompanies([]);
      } finally {
        setIsLoadingCompanies(false);
      }
    }, 300);

    return () => clearTimeout(handle);
  }, [searchTerm, token]);

  const hasError = (k: keyof typeof errors) => Boolean(touched[k] && errors[k]);
  const allValid = Object.values(errors).every((e) => !e);

  const onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    const { name, value } = e.target;
    setValues((s) => ({ ...s, [name]: value }));
  };

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setTouched({
      callDate: true,
      callTime: true,
      callReason: true,
      firstName: true,
      lastName: true,
      email: true,
      orgName: true,
      employees: true,
      phone: true,
    });
    if (!allValid) return;

    try {
      setIsSubmitting(true);

      const path = "form";
      console.log({ token });
      const body = { values, referer };
      const arg = { token, body };
      const response = await afasPoster(path, { arg });
      console.log(response);

      alert(`Bedankt! We nemen contact met je op.\n\n${JSON.stringify(values, null, 2)}`);
    } catch (error) {
      console.error("Error while submitting form", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: { main: "#0A67B2" }, // header/button blue
          warning: { main: "#F29F05" }, // orange accents
        },
        components: {
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                backgroundColor: "#fff8ef",
                borderRadius: 8,
                "& fieldset": {
                  borderColor: "#F29F05",
                },
                "&:hover fieldset": {
                  borderColor: "#e38f00",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#e38f00",
                  borderWidth: 2,
                },
              },
              notchedOutline: {
                borderColor: "#F29F05",
              },
            },
          },
          MuiSelect: {
            styleOverrides: {
              outlined: {
                backgroundColor: "#fff8ef",
                borderRadius: 8,
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: 8,
                textTransform: "none",
                fontWeight: 600,
              },
            },
          },
        },
      }),
    [],
  );

  console.log({ suggestedCompanies });

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper elevation={1} sx={{ borderRadius: 2, overflow: "hidden" }}>
          {/* Header */}
          <Box sx={{ bgcolor: "primary.main", color: "primary.contrastText", p: { xs: 2, sm: 3 } }}>
            <Typography variant="h3" component="h1" fontWeight={800}>
              Maak een afspraak
            </Typography>
          </Box>

          {/* Form content */}
          <Box component="form" noValidate onSubmit={onSubmit} sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Uiteraard kun je ons ook direct bereiken op{" "}
              <Box
                component="a"
                href="tel:0334343883"
                sx={{ color: "primary.main", textDecoration: "underline" }}
              >
                033 - 434 3883
              </Box>
              . Kies hieronder een geschikt moment om teruggebeld te worden.
            </Typography>

            <Box display="grid" gap={2.5}>
              {/* Call date */}
              <TextField
                required
                label="Bel mij op"
                name="callDate"
                type="date"
                disabled={isSubmitting}
                value={values.callDate}
                onChange={onChange}
                onBlur={() => setTouched((t) => ({ ...t, callDate: true }))}
                error={hasError("callDate")}
                helperText={hasError("callDate") ? errors.callDate : " "}
                InputLabelProps={{ shrink: true }}
                // InputProps={{
                //   endAdornment: (
                //     <InputAdornment position="end">
                //       <EventIcon />
                //     </InputAdornment>
                //   ),
                // }}
              />

              {/* Call time */}
              <FormControl required error={hasError("callTime")}>
                <InputLabel id="callTime-label">Voorkeur tijd</InputLabel>
                <Select
                  labelId="callTime-label"
                  label="Voorkeur tijd"
                  disabled={isSubmitting}
                  value={values.callTime}
                  onChange={(e) => setValues((s) => ({ ...s, callTime: String(e.target.value) }))}
                  onBlur={() => setTouched((t) => ({ ...t, callTime: true }))}
                  IconComponent={AccessTimeIcon}
                >
                  <MenuItem value="">
                    <em>Selecteer een tijd</em>
                  </MenuItem>
                  {callTimeOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{hasError("callTime") ? errors.callTime : " "}</FormHelperText>
              </FormControl>

              {/* Call reason */}
              <FormControl required error={hasError("callReason")}>
                <InputLabel id="callReason-label">Reden afspraak</InputLabel>
                <Select
                  labelId="callReason-label"
                  label="Reden afspraak"
                  disabled={isSubmitting}
                  value={values.callReason}
                  onChange={(e) => setValues((s) => ({ ...s, callReason: String(e.target.value) }))}
                  onBlur={() => setTouched((t) => ({ ...t, callReason: true }))}
                >
                  <MenuItem value="">
                    <em>Kies een reden</em>
                  </MenuItem>
                  {callReasonOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{hasError("callReason") ? errors.callReason : " "}</FormHelperText>
              </FormControl>

              {/* Voornaam */}
              <TextField
                required
                label="Voornaam"
                name="firstName"
                disabled={isSubmitting}
                value={values.firstName}
                onChange={onChange}
                onBlur={() => setTouched((t) => ({ ...t, firstName: true }))}
                error={hasError("firstName")}
                helperText={hasError("firstName") ? errors.firstName : " "}
              />

              {/* Achternaam */}
              <TextField
                required
                label="Achternaam"
                name="lastName"
                disabled={isSubmitting}
                value={values.lastName}
                onChange={onChange}
                onBlur={() => setTouched((t) => ({ ...t, lastName: true }))}
                error={hasError("lastName")}
                helperText={hasError("lastName") ? errors.lastName : " "}
              />

              {/* Naam organisatie */}
              <Autocomplete<CompanyInfo, false, false, true>
                disablePortal
                freeSolo
                options={suggestedCompanies ?? []}
                getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
                inputValue={companyInput}
                onInputChange={(_, newInputValue, reason) => {
                  setCompanyInput(newInputValue);
                  setValues((s) => ({ ...s, orgName: newInputValue }));

                  if (reason === "input") {
                    const alreadyExists = suggestedCompanies.some(
                      (c) => c.name.toLowerCase() === newInputValue.toLowerCase(),
                    );

                    if (!alreadyExists) {
                      setSearchTerm(newInputValue);
                    }
                  }
                }}
                value={null}
                onChange={(_, newValue) => {
                  if (!newValue) {
                    setCompanyInput("");
                    setValues((s) => ({ ...s, orgName: "" }));
                    return;
                  }

                  if (typeof newValue === "string") {
                    setCompanyInput(newValue);
                    setValues((s) => ({ ...s, orgName: newValue }));
                    return;
                  }

                  setValues((s) => ({ ...s, orgName: newValue.name }));
                  setCompanyInput(newValue.name);
                }}
                onBlur={() => setTouched((t) => ({ ...t, orgName: true }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    disabled={isSubmitting}
                    required
                    label="Naam organisatie"
                    error={hasError("orgName")}
                    helperText={hasError("orgName") ? errors.orgName : " "}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingCompanies && <CircularProgress size={20} sx={{ mr: 1 }} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {/* Aantal medewerkers */}
              <FormControl required error={hasError("employees")}>
                <InputLabel id="employees-label">Aantal medewerkers</InputLabel>
                <Select
                  labelId="employees-label"
                  label="Aantal medewerkers"
                  disabled={isSubmitting}
                  value={values.employees}
                  onChange={(e) => setValues((s) => ({ ...s, employees: String(e.target.value) }))}
                  onBlur={() => setTouched((t) => ({ ...t, employees: true }))}
                >
                  <MenuItem value={"1-10"}>1–10</MenuItem>
                  <MenuItem value={"11-50"}>11–50</MenuItem>
                  <MenuItem value={"51-200"}>51–200</MenuItem>
                  <MenuItem value={"201-500"}>201–500</MenuItem>
                  <MenuItem value={">500"}>&gt; 500</MenuItem>
                </Select>
                <FormHelperText>{hasError("employees") ? errors.employees : " "}</FormHelperText>
              </FormControl>

              {/* E-mail */}
              <TextField
                required
                label="E-mail"
                name="email"
                disabled={isSubmitting}
                value={values.email}
                onChange={onChange}
                onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                placeholder="mail@example.com"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <MailOutlineIcon />
                    </InputAdornment>
                  ),
                }}
                error={hasError("email")}
                helperText={hasError("email") ? errors.email : " "}
              />

              {/* Telefoonnummer */}
              <TextField
                required
                label="Telefoonnr."
                name="phone"
                disabled={isSubmitting}
                placeholder="+31612345678"
                value={values.phone}
                onChange={onChange}
                onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <PhoneOutlinedIcon />
                    </InputAdornment>
                  ),
                }}
                error={hasError("phone")}
                helperText={
                  hasError("phone") ? (
                    errors.phone
                  ) : (
                    <Tooltip title="Bijv. +31612345678 of 0612345678">
                      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 16, verticalAlign: "middle" }} />
                        <span>Bijv. +31612345678</span>
                      </Box>
                    </Tooltip>
                  )
                }
              />

              <Box display="flex" justifyContent="flex-end" mt={1}>
                <Button type="submit" variant="contained" size="large" disabled={!allValid || isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Bezig met verzenden...
                    </>
                  ) : (
                    "Mijn afspraak maken"
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
    </ThemeProvider>
  );
}
